import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { AssetSvg } from "../../assets";
import { useFunctionalityCallRecording } from "../../pages/hooks";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callId?: string;
  title?: string;
};

export function ModalCallRecording(props: Props): JSX.Element {
  const { show, handleShow, callId, title } = props;
  const { LoadByCallId, LoadAccessUrl } = useFunctionalityCallRecording();
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recordingId, setRecordingId] = useState<string>("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (!show || !callId) return;
    setIsLoading(true);
    setHasError(false);
    setAudioUrl("");
    setDownloadUrl("");
    LoadByCallId(callId)
      .then((data) => {
        const first = Array.isArray(data) ? data[0] : data;
        const url = first?.accessUrl ?? "";
        const id = first?.id ?? "";
        setAudioUrl(url);
        setDownloadUrl("");
        setRecordingId(id);
      })
      .catch(() => {
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [LoadByCallId, callId, show]);

  const canDownload = Boolean(recordingId);

  const handleDownload = async () => {
    if (!recordingId || downloadLoading) return;
    setDownloadLoading(true);
    try {
      const data = await LoadAccessUrl(recordingId, true, 30);
      const url = data?.url ?? "";
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.rel = "noopener";
        anchor.target = "_blank";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch {
      // no-op; error message is handled by backend errors and user can retry
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => handleShow(false)}
      id="callRecordingModal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title ?? "Call Recording"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-muted">Loading recording...</div>
        ) : hasError ? (
          <div className="text-danger">Failed to load recording.</div>
        ) : audioUrl ? (
          <div className="d-flex align-items-center gap-2">
            <audio
              controls
              controlsList="nodownload"
              className="flex-grow-1 mr-3"
              style={{ marginTop: 8 }}
            >
              <source src={audioUrl} />
              Your browser does not support the audio element.
            </audio>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center mt-2"
              style={{ borderRadius: 10, padding: 14, boxShadow: "0 6px 16px rgba(0, 0, 0, 0.25)" }}
              title="Download"
              onClick={handleDownload}
              disabled={!canDownload || downloadLoading}
            >
              <span className="" style={{ lineHeight: 0 }}>
                <AssetSvg.Download style={{ width: 16, height: 16 }} />
              </span>
            </button>
          </div>
        ) : (
          <div className="text-muted">No recording is available for this call.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary text-uppercase"
          onClick={() => handleShow(false)}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}
